import React from 'react'

export interface TotalProjectsBarData {
  month: string
  value: number
  isHighlighted?: boolean
}

interface TotalProjectsGraphProps {
  data?: TotalProjectsBarData[]
  className?: string
}

const SLOT_CONFIG = [
  { left: 0, highlighted: false },
  { left: 71, highlighted: false },
  { left: 142, highlighted: true },
]

/**
 * 🔴 There is no fallback data. There used to be.
 *
 * `DEFAULT_DATA` was a plausible three-month bar chart with real month labels
 * (`Oct 52, Nov 38, Dec 72`), applied by a default parameter — so ANY caller
 * that passed nothing, or an empty array, rendered an invented trend beneath a
 * real headline figure. `OTCMetricsCards` passed `undefined` deliberately.
 * A chart with nothing to plot now renders nothing, and the slot keeps its
 * height so the card does not reflow.
 */
const TotalProjectsGraph: React.FC<TotalProjectsGraphProps> = ({ data, className = '' }) => {
  const displayedData = (data ?? []).slice(-3)
  if (displayedData.length === 0) {
    return <div className={`relative h-[100px] w-full max-w-[214px] ${className}`} aria-hidden="true" />
  }

  const values = displayedData.map((item) => item.value)
  const minValue = Math.min(...values)
  const maxValue = Math.max(...values)
  const range = maxValue - minValue || 1

  return (
    <div className={`relative h-[100px] w-full max-w-[214px] ${className}`}>
      {displayedData.map((item, index) => {
        const cfg = SLOT_CONFIG[index] ?? SLOT_CONFIG[SLOT_CONFIG.length - 1]
        const isHighlighted = item.isHighlighted ?? cfg.highlighted
        const normalized = range <= Number.EPSILON ? 0.5 : (item.value - minValue) / range
        const height = Math.round(28 + normalized * 44)
        const top = 100 - height
        const labelTop = Math.min(84, top + 10)

        return (
          <div
            key={`${item.month}-${index}`}
            className="absolute h-full w-[72px]"
            style={{ left: `${cfg.left}px` }}
          >
            <div
              className="absolute inset-0 border-l border-dashed border-[rgba(45,41,35,0.32)]"
              style={{ borderRightWidth: index === displayedData.length - 1 ? '1px' : '0' }}
            />

            <div
              className={`absolute left-[3px] w-[64px] rounded-t-[2px] ${
                isHighlighted
                  ? 'bg-gradient-to-b from-[rgba(227,253,90,0.28)] to-transparent'
                  : 'bg-gradient-to-b from-[#f7f7f7] dark:from-[rgba(197,204,159,0.32)] to-transparent'
              }`}
              style={{
                top: `${top}px`,
                height: `${height}px`,
              }}
            />

            <div
              className={`absolute left-[3px] h-1 w-[64px] rounded-[8px] ${
                isHighlighted ? 'bg-[#dffb2c]' : 'bg-[#f5f5f5] dark:bg-[#cccdc5]'
              }`}
              style={{ top: `${top}px` }}
            />

            <p
              className={`absolute left-1/2 w-[64px] -translate-x-1/2 text-center font-plus-jakarta text-[12px] font-normal leading-[16px] ${
                isHighlighted ? 'text-[#2d2923] dark:text-[#fdfdfd]' : 'text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]'
              }`}
              style={{ top: `${labelTop}px` }}
            >
              {item.month}
            </p>
          </div>
        )
      })}
    </div>
  )
}

export default TotalProjectsGraph
